import { computed, type ComputedRef, type Ref } from 'vue';

import {
  AssessmentType,
  BorrowerType,
  ExitStrategyValue,
  LoanType,
  TrusteeType,
  type AssessmentTypeEnum,
} from './const';

import type { BorrowerGroup } from '@/types/BorrowerGroup';
import type { LoanApplication } from '@/types';
import { hasAssets } from '@/helpers/typeGuards';

/**
 * Checks if a company has a registered ABN and is actively trading.
 * Used to determine if Net Profit should be collected.
 */
const isActiveCompany = (company?: { hasRegisteredABN?: boolean; isTrading?: boolean }): boolean =>
  company?.hasRegisteredABN === true && company?.isTrading === true;

/**
 * Checks if a borrower group is a corporate trust (trust with company trustee).
 */
const isCorporateTrust = (group: BorrowerGroup): boolean =>
  group.type === BorrowerType.TRUST && group.trustDetails?.trusteeType === TrusteeType.COMPANY;

/**
 * Gets the company details for a borrower group.
 * For companies, returns companyDetails directly.
 * For corporate trusts, returns the trustee company details.
 */
const getCompanyDetails = (group: BorrowerGroup) => {
  if (group.type === BorrowerType.COMPANY) {
    return group.companyDetails;
  }
  if (isCorporateTrust(group)) {
    return group.trustDetails?.companyDetails;
  }
  return undefined;
};

interface FinancialCollectionRules {
  // Assessment and loan state
  isRegulated: ComputedRef<boolean>;
  hasEndDebt: ComputedRef<boolean>;
  isSingleSecurity: ComputedRef<boolean>;
  isExitStrategySaleOfAssets: ComputedRef<boolean>;

  // High-level step visibility
  shouldShowIncomeStep: ComputedRef<boolean>;
  shouldShowExpensesStep: ComputedRef<boolean>;
  shouldShowAssetsStep: ComputedRef<boolean>;

  // What to collect at the step level
  shouldCollectCompanyNetProfit: ComputedRef<boolean>;
  shouldCollectIndividualIncome: ComputedRef<boolean>;
  shouldCollectExpenses: ComputedRef<boolean>;
  shouldCollectAssets: ComputedRef<boolean>;
  shouldCollectLiabilities: ComputedRef<boolean>;

  // Per-borrower group checks
  shouldShowNetProfitForGroup: (group: BorrowerGroup) => boolean;
  shouldShowIndividualIncomeForGroup: (group: BorrowerGroup) => boolean;
  shouldShowAssetsForGroup: (group: BorrowerGroup) => boolean;

  // Servicing check (for Unregulated + End Debt)
  needsServicingCheck: ComputedRef<boolean>;
  servicingCheckFailed: ComputedRef<boolean>;
}

/**
 * Composable for determining financial collection rules based on:
 * - Assessment type (regulated vs unregulated)
 * - End debt status
 * - Loan type (BNSL vs Single Security)
 * - Exit strategy
 * - Servicing check results (netSurplus)
 *
 * @see documentation/feature-financial-collection-rules.md
 */
export function useFinancialCollectionRules(loanApp: Ref<LoanApplication>): FinancialCollectionRules {
  // Base state computations
  const assessmentType = computed<AssessmentTypeEnum>(() => loanApp.value.assessmentType ?? AssessmentType.REGULATED);

  const isErhl = computed(() => loanApp.value.lendingProduct === 'erhl');

  const isRegulated = computed(() => assessmentType.value === AssessmentType.REGULATED);

  const hasEndDebt = computed(() => (loanApp.value.endDebt ?? 0) > 0);

  const isSingleSecurity = computed(() => loanApp.value.loanType === LoanType.SINGLE_SECURITY);

  const isExitStrategySaleOfAssets = computed(
    () => loanApp.value.exitStrategy === ExitStrategyValue.SALE_OF_NON_SECURITY_ASSETS,
  );

  // Servicing check (for Unregulated + End Debt scenarios)
  const needsServicingCheck = computed(() => !isErhl.value && !isRegulated.value && hasEndDebt.value);

  const servicingCheckFailed = computed(() => {
    if (!needsServicingCheck.value) return false;
    const netSurplus = loanApp.value.netSurplus ?? 0;
    return netSurplus <= 0;
  });

  // Special case: Single Security + No End Debt + Exit Strategy = Sale of Assets
  const isAssetsOnlyScenario = computed(
    () => isSingleSecurity.value && !hasEndDebt.value && isExitStrategySaleOfAssets.value,
  );

  // Step visibility
  const shouldShowIncomeStep = computed(() => {
    // Assets-only scenario: skip income
    if (isAssetsOnlyScenario.value) return false;
    // No end debt (other cases): skip all financial collection
    if (!hasEndDebt.value) return false;
    // End debt scenarios: show income
    return true;
  });

  const shouldShowExpensesStep = computed(() => {
    // Assets-only scenario: skip expenses
    if (isAssetsOnlyScenario.value) return false;
    // No end debt: skip all financial collection
    if (!hasEndDebt.value) return false;
    // Unregulated: NEVER collect expenses
    if (!isRegulated.value) return false;
    // Regulated + End Debt: show expenses
    return true;
  });

  const shouldShowAssetsStep = computed(() => {
    // Assets-only scenario: show assets
    if (isAssetsOnlyScenario.value) return true;
    // Show Assets step if loan application has any assets
    if (hasAssets(loanApp.value)) return true;
    // No end debt (other cases): skip all financial collection
    if (!hasEndDebt.value && !isAssetsOnlyScenario.value) return false;
    // End debt scenarios: show assets
    return true;
  });
  const shouldCollectCompanyNetProfit = computed(() => {
    if (isErhl.value) return true;
    if (isAssetsOnlyScenario.value) return false;
    if (!hasEndDebt.value) return false;
    return true;
  });

  const shouldCollectIndividualIncome = computed(() => {
    if (isErhl.value) return true;
    if (isAssetsOnlyScenario.value) return false;
    if (!hasEndDebt.value) return false;
    if (isRegulated.value) return true;
    return true;
  });

  const shouldCollectExpenses = computed(() => {
    if (isErhl.value) return true;
    if (isAssetsOnlyScenario.value) return false;
    if (!hasEndDebt.value) return false;
    if (!isRegulated.value) return false;
    return true;
  });

  const shouldCollectAssets = computed(() => {
    if (isErhl.value) return true;
    if (isAssetsOnlyScenario.value) return true;
    if (hasAssets(loanApp.value)) return true;
    if (!hasEndDebt.value) return false;
    return true;
  });

  const shouldCollectLiabilities = computed(() => {
    if (isErhl.value) return true;
    if (isAssetsOnlyScenario.value) return false;
    if (!hasEndDebt.value) return false;
    return true;
  });
  /**
   * Determines if Net Profit form should be shown for a specific borrower group.
   * Net Profit is shown when:
   * - The group is a Company with registered ABN and is trading
   * - OR the group is a Trust with Company trustee that has ABN and is trading
   */
  const shouldShowNetProfitForGroup = (group: BorrowerGroup): boolean => {
    // Check overall conditions first
    if (!shouldCollectCompanyNetProfit.value) return false;

    // Individual borrowers never show Net Profit
    if (group.type === BorrowerType.INDIVIDUAL) return false;

    // Trust with individual trustees never shows Net Profit
    if (group.type === BorrowerType.TRUST && group.trustDetails?.trusteeType === TrusteeType.INDIVIDUAL) {
      return false;
    }

    // Company or Corporate Trust: check if trading
    const companyDetails = getCompanyDetails(group);
    return isActiveCompany(companyDetails);
  };

  /**
   * Determines if individual income forms should be shown for a borrower group's applicants.
   *
   * Logic varies by scenario:
   * - Regulated + End Debt: Always show individual income
   * - Unregulated + End Debt:
   *   - If company is NOT trading: show individual income for directors
   *   - If company IS trading: only show if servicing check failed (netSurplus < 0)
   */
  const shouldShowIndividualIncomeForGroup = (group: BorrowerGroup): boolean => {
    if (!shouldCollectIndividualIncome.value) return false;

    if (isErhl.value) return true;

    if (group.type === BorrowerType.INDIVIDUAL) {
      return isRegulated.value || servicingCheckFailed.value;
    }

    if (group.type === BorrowerType.TRUST && group.trustDetails?.trusteeType === TrusteeType.INDIVIDUAL) {
      return true;
    }

    const companyDetails = getCompanyDetails(group);
    const isTrading = isActiveCompany(companyDetails);

    if (isRegulated.value) return true;

    if (!isTrading) return true;
    return servicingCheckFailed.value;
  };

  /**
   * Determines if assets/liabilities should be collected for a borrower group's applicants.
   *
   * Logic varies by scenario:
   * - Regulated + End Debt: Always collect assets/liabilities
   * - Unregulated + End Debt:
   *   - If company is NOT trading: collect assets/liabilities for directors
   *   - If company IS trading: only collect if servicing check failed (netSurplus < 0)
   *   - Individual trustees: do NOT collect (they have no company to contribute Net Profit)
   */
  const shouldShowAssetsForGroup = (group: BorrowerGroup): boolean => {
    if (!shouldCollectAssets.value) return false;

    if (isErhl.value) return true;

    if (group.type === BorrowerType.INDIVIDUAL) {
      return isRegulated.value || servicingCheckFailed.value;
    }

    if (group.type === BorrowerType.TRUST && group.trustDetails?.trusteeType === TrusteeType.INDIVIDUAL) {
      return true;
    }

    const companyDetails = getCompanyDetails(group);
    const isTrading = isActiveCompany(companyDetails);

    if (isRegulated.value) return true;

    if (!isTrading) return true;
    return servicingCheckFailed.value;
  };

  return {
    // State
    isRegulated,
    hasEndDebt,
    isSingleSecurity,
    isExitStrategySaleOfAssets,

    // Step visibility
    shouldShowIncomeStep,
    shouldShowExpensesStep,
    shouldShowAssetsStep,

    // What to collect
    shouldCollectCompanyNetProfit,
    shouldCollectIndividualIncome,
    shouldCollectExpenses,
    shouldCollectAssets,
    shouldCollectLiabilities,

    // Per-group checks
    shouldShowNetProfitForGroup,
    shouldShowIndividualIncomeForGroup,
    shouldShowAssetsForGroup,

    // Servicing check
    needsServicingCheck,
    servicingCheckFailed,
  };
}
